/**
 * Customer Quotation — `quoflow.quoflow.quoflow.api.quotation.*` (Task 11). Live shapes verified
 * 2026-08-04:
 *
 *   GET   quotation.get_quotation?name=        {name, status, revision, customer, customer_name,
 *                                                enquiry, grand_total, markup, management_commission,
 *                                                convenience_amount, items: [...], revision_log: []}
 *   PATCH quotation.update_quotation_line       {quotation, line_id, data: {margin, sell_unit}}
 *   POST  quotation.send_quotation              {quotation, to, subject, note} → {status, sent_at,
 *                                                 violations: []}
 *   POST  quotation.decide_quotation            {quotation, decision} → {quotation, status}
 *
 * Follows the pattern `useComparison.ts` set: a `Wire*` interface per response, a pure exported
 * `toX` converter, no shared mapper file. `mappers/quotation.ts` (the old Frappe-doctype-gateway
 * mapper this file used to call through `getDoc`/`getList`) is RETIRED — nothing imports it any
 * more.
 *
 * ---- resolving "the quotation for enquiry X" — a doctype read, not a method call ----
 *
 * `get_quotation` takes the document's own `name`, and the enquiry itself carries no link back
 * to it (`EnquirySummary.quotation_id` is hardcoded `null` — `useEnquiries.ts`'s `get_enquiries`
 * has no such field either, a Task 7 gap), so the name has to come from somewhere else first.
 * The obvious candidate, `comparison.build_quotation` (Task 10, get-or-create), CANNOT be used:
 * `comparison.py:924` imports `quoflow.quoflow.quoflow.quoflow.api.quotation`, four segments
 * where the module has three — the same one-too-many-`quoflow` defect as B1/B2, in a third
 * place. It always throws, live.
 *
 * `findQuotationName` (below) routes around it instead: a plain GET against the `Quotation`
 * DOCTYPE — `getResource('Quotation', { filters: [['enquiry', '=', enquiryId]], fields: ['name'],
 * limit: 1 })`, the SAME `/api/resource` transport `useEnquiryFromMail.ts`/`useItems.ts` use, not
 * the `quoflow.quotation.*` method namespace. This is a real read with NO side effect — it either
 * finds a name or it doesn't, and nothing is created either way. `loadQuotationForEnquiry` calls
 * it and then `readQuotation(name)` (the `get_quotation` GET) when a name was found, throwing
 * `useQuotation`'s "no quotation yet" error otherwise. Creating one is a SEPARATE flow entirely —
 * `ComparePage`'s `useCreateQuotation` → `quotationFromAllocation.ts` → `quotation.create_quotation`
 * — triggered by the comparison screen's "Build Quotation" button, never by either hook below.
 *
 * ---- `useExistingQuotation` never builds, and never routes through `loadQuotationForEnquiry`'s throw ----
 *
 * `useExistingQuotation` (the read-only hook `RevisionsPage` uses so opening Revisions can never
 * CREATE a quotation) calls `findQuotationName` directly rather than sharing
 * `loadQuotationForEnquiry`: `name === null` is reported as `null` (RevisionsPage's real "no
 * quotation yet" state), and a `name` is loaded through the same `readQuotation`. Deliberately NOT
 * a try/catch around `loadQuotationForEnquiry` — that used to swallow every error to `null`,
 * including the lookup itself failing, which left a genuinely-failing request looking like a
 * successful "nothing found" load to the polling guard below (fix wave, 2026-08-08).
 *
 * ---- money has no currency field on this wire ----
 *
 * Unlike the OLD `Quotation` doctype (`customer_currency`/`po_currency`/`exchange_rate`),
 * `get_quotation` carries no currency anywhere — every `cost_unit`/`sell_unit`/`grand_total` is a
 * bare number with nothing to label it. Every `Money` this mapper produces is labelled
 * `getBaseCurrency()`; `fx` is always `{}` (there is no snapshot to read one from — a screen
 * showing another currency converts through `FX_FALLBACK`, same as before). BACKEND FOLLOW-UP:
 * ask for a currency field alongside these numbers.
 *
 * ---- totals are recomputed here, not read off the stored `grand_total` ----
 *
 * The binding rule for this task: "the FE calculates margins and totals and the backend stores
 * what it is sent — no server recalculation." `grand_total` is therefore only an ECHO of
 * whatever this app last computed and PATCHed — potentially stale the instant a line changes,
 * since `update_quotation_line` writes a single line, not the header totals. Trusting it would
 * reintroduce exactly the staleness the old file's PREVIEW-vs-SAVED split existed to paper over.
 * Instead this mapper calls the SAME `previewTotals` (`lib/pricing.ts`) the live-editing preview
 * already uses — one implementation of "what does this quotation currently total", matching
 * CLAUDE.md's "shared derivations go in lib/" rule. `commission_pct`/`convenience`/`markup_pct`
 * ARE trusted off the wire (`management_commission`/`convenience_amount`/`markup`) — they are
 * RATES the server (or, in the mock, the settings) hold as configuration, echoed back verbatim,
 * not derived totals `previewTotals` would otherwise be re-deriving from a stale copy of itself.
 * `markup` is the top-level uplift `previewTotals` applies to the whole subtotal (task 1) — it is
 * not the same thing as any per-line `default_margin_pct`, which is already baked into that
 * line's own `sell_unit` and must never be read again here as if it were the header rate.
 *
 * ---- every line is 'rfq' ----
 *
 * `QuotationLine.tag` has no evidence field on this wire at all (the old `Quotation Item` had
 * `supplierquotecount`/`lowest_quote_supplier` to infer it from — `mappers/quotation.ts`, point
 * 2). `build_quotation` composes a document only from the comparison's chosen allocation
 * (RFQ-routed lines — `comparison.get_comparison` filters `route = 'rfq'`), so every line this
 * API can produce today is RFQ-sourced; a contract-priced provision line has no composition path
 * here yet. `category` stays omitted (Quoflow has no category concept — Q15/B2).
 */

import { withoutInternalAnnotations } from '../internalNotes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { callGet, callPatch, callPost, callPut } from './bosun'
import { withDocumentLock } from './serialWrites'
import { advanceToComparisonDone } from './advanceStatus'
import { markSentPatch } from '../markSent'
import { shouldAdvanceTo } from '../statusFlow'
import { buildUpdateEnquiryWireBody } from './useEnquiryMutations'
import { totalCommissionPct } from '../quotationCommission'
import { getResource } from './frappe'
import { api as legacyApi } from './client'
import { BosunApiError } from './errors'
import { money, num, str, type WireFx } from './wire'
import { getBaseCurrency } from './baseCurrency'
import { convert } from '../fx'
import { markupPct, previewTotals } from '../pricing'
import { convertedNetTotal, convertFixedAmounts, convertItems, pinPoSide, type WireItemRow } from '../quotationCurrency'
import { qk } from './keys'
import { pushEnquiryChain } from './g6Push'
import { POLL_MS } from './polling'
import type { CreateQuotationPayload } from '../quotationFromAllocation'
import { completeQuotationPayload } from '../quotationPayload'
import { describeMandatory, fillFromEnquiry, mandatoryFieldsFrom, missingQuotationMandatory, QUOTATION_MANDATORY, type EnquiryHeaderSource } from '../quotationMandatory'
import type { ChargeType, QuotationTaxRow, Currency, DecideResponse, EnquiryStatus, Money, Quotation, QuotationLine, QuotedItem } from '../types'

/* ============================================================================================
 * quotation.get_quotation → useQuotation / useExistingQuotation
 * ============================================================================================ */

export interface WireQuotationItem {
  /** 'Provision' | 'Base Price' | 'Supplier' — the line's route. See `tagFromSource`. */
  source?: unknown
  name: string
  item_code: string
  enquiry_row_no: string
  qty: number
  uom: string
  cost_unit: number
  margin: number
  sell_unit: number
  profit_percentage: number
  amount: number
  /* ---- Printed fields and the PO side (2026-09-14) — all on the wire, all writable ---- */
  item_name?: string | null
  description?: string | null
  offer_remarks?: string | null
  /** The supplier's currency. Absent on older documents — read as the quotation's. */
  po_currency?: string | null
  /** The supplier's own unit rate in `po_currency`. NOT an exchange rate, despite the name. */
  po_currency_rate?: number | null
  /** `po_currency` → quotation currency: `po_currency_rate × conversion_rate = cost_unit`. */
  conversion_rate?: number | null
}

export interface WireQuotation {
  name: string
  status: string
  revision: number
  customer: string
  customer_name?: string | null
  /* The quotation's own header (Akhil, 2026-09-16) — all real fields on the doctype, all
     round-tripping through `update_quotation` (probed on QTN-2026-00050, reverted). */
  sales_person?: string | null
  purchaser?: string | null
  customer_reference_no?: string | null
  /** Mandatory since 2026-09-17 — the enquiry's company. Older documents carry none until healed. */
  company?: string | null
  /** Link → Enquiry Type, mandatory since 2026-09-17 (pm). */
  enquiry_type?: string | null
  /** `delivey_time` — sic, the doctype's own spelling (checked 2026-09-16). Data, free text. */
  delivey_time?: string | null
  /** Select: Sales · Service · Maintenance · Shopping Cart — mandatory, defaults to Sales. */
  order_type?: string | null
  /**
   * OPTIONAL because it is unconfirmed on the response. The field exists on the `Quotation`
   * doctype and `create_quotation` is sent it; whether `get_quotation` echoes it has NOT been
   * measured. Absent, the fallback in `toQuotation` applies.
   */
  customer_currency?: string | null
  /** Company → `customer_currency` rate, as stored at build/re-price. `1` or absent when same. */
  exchange_rate?: number | null
  enquiry: string
  grand_total: number
  markup: number
  management_commission: number
  convenience_amount: number
  /* The rest of the commission panel and the discount block — all real fields on the doctype,
     none of which this mapper read until 2026-09-04. Optional because a payload predating them,
     or the mock, may carry none. */
  software_commission?: number
  port_agent_commission?: number
  other_charges?: number
  /**
   * The LINE markup and commission, stored per quotation — added by the Frappe team 2026-09-07
   * at our request, and they are INERT: measured live on QTN-2026-00012, writing
   * `line_markup: 30, line_commission: 40` left `grand_total` equal to `net_total` (2539.89).
   *
   * That is the whole point of them. `markup` and the four commission fields beside them are the
   * document-level CHARGE (`D27`) — writing a rate there bills the customer for it a second time
   * on top of line prices that already contain it. These two only record what was applied, so the
   * rates finally survive a reload.
   */
  line_markup?: number
  line_commission?: number
  total_commission?: number
  net_total?: number
  total?: number
  quotation_markup?: number
  overall_margin?: number
  total_taxes_and_charges?: number
  base_total?: number
  base_net_total?: number
  base_grand_total?: number
  base_total_taxes_and_charges?: number
  total_po_cost?: number
  base_discount_amount?: number | null
  po_currency?: string | null
  /**
   * The discount basis — `apply_discount_on` on THIS doctype (measured 2026-09-15: persists under
   * that name, is dropped under `apply_additional_discount_on`). The old name is still read as a
   * fallback for any document the mock or an older build wrote.
   */
  apply_discount_on?: string | null
  apply_additional_discount_on?: string | null
  additional_discount_percentage?: number
  additional_discount_amount?: number
  taxes?: WireTaxRow[]
  tc_name?: string | null
  terms?: string | null
  /** On the wire (QTN-2026-00017), both writable — see `QuotationTerms`. */
  payment_terms_template?: string | null
  customer_address?: string | null
  /** Logistics, copied from the enquiry at build and editable here since 2026-09-15. Links. */
  vessel?: string | null
  port?: string | null
  delivery_location?: string | null
  items: WireQuotationItem[]
  revision_log?: unknown[]
}

/** One `Sales Taxes and Charges` row, as `get_quotation` returns it. */
export interface WireTaxRow {
  name?: string
  idx?: number
  charge_type?: string
  account_head?: string | null
  tax_rate?: number
  tax_amount?: number
  row_id?: string | null
  total?: number
}

/** Anything the doctype's Select does not allow is not a charge type; `Actual` is the inert one. */
const CHARGE_TYPES = new Set<string>([
  'Actual',
  'On Net Total',
  'On Previous Row Amount',
  'On Previous Row Total',
  'On Item Quantity',
])

export function toTaxRow(w: WireTaxRow, index: number): QuotationTaxRow {
  const idx = num(w.idx) || index + 1
  return {
    // The child docname when the server has one; a stable synthetic key otherwise, because React
    // needs a key before a newly added row has been saved.
    id: str(w.name) || `new-${idx}`,
    idx,
    // An unrecognised charge type falls back to `Actual`, which computes nothing — far safer than
    // guessing at a percentage rule and inventing a charge.
    charge_type: CHARGE_TYPES.has(str(w.charge_type)) ? (str(w.charge_type) as ChargeType) : 'Actual',
    account_head: str(w.account_head),
    tax_rate: num(w.tax_rate),
    tax_amount: num(w.tax_amount),
    row_id: str(w.row_id),
    total: num(w.total),
  }
}

/**
 * `Quotation.status` — MEASURED off the doctype 2026-08-09, not carried over.
 *
 * The previous map was inherited from `mappers/quotation.ts` and expected
 * `Draft | Submitted | Ordered | Cancelled | Expired`. The field's real options are:
 *
 *     Draft · Quoted · Won · Rejected · Cancelled
 *
 * Only `Draft` lined up. `Quoted`, `Won` and `Rejected` were absent from the map, so
 * `toQuotationStatus` fell through to its `'draft'` default for ALL THREE — every quotation
 * that had been sent, won or rejected read back as a draft. Sending one and reloading showed it
 * unsent; the Kanban's Won/Rejected decision looked undone the moment the card refreshed; and
 * `QuotePage`'s "Already {status}" guard never fired, so a sent quotation could be sent again
 * and again. `Cancelled` mapped to `Rejected`, conflating two states the doctype keeps apart.
 *
 * The fallback is the reason this went unnoticed: an unknown status is not an error here, it is
 * silently a draft. Kept — a status we cannot read should not blank the screen — but it is only
 * safe now that the map is complete.
 */
const STATUS_MAP: Record<string, Quotation['status']> = {
  Draft: 'draft',
  Quoted: 'Quoted',
  Won: 'Won',
  Rejected: 'Rejected',
  Cancelled: 'Archived',
}

/** Exported so `useRevisions.ts`'s `apply_revisions` (same doctype's status, same vocabulary)
 * doesn't duplicate this map. */
export function toQuotationStatus(value: unknown): Quotation['status'] {
  return STATUS_MAP[str(value)] ?? 'draft'
}

/**
 * A line carries TWO currencies, and conflating them misstates the margin.
 *
 * `quotationFromAllocation.ts` converts the SELL price into the customer's currency and leaves
 * `cost_unit` in the company's — cost is what we are actually charged, and re-expressing it would
 * make it a derived figure rather than a fact. So a SAR customer's line is `cost 12.00 AED` /
 * `sell 14.08 SAR`, and labelling both the same way prints a margin that is neither.
 */
/**
 * A line's ROUTE, from the one field on the wire that records it.
 *
 * `tag` used to be hardcoded to `'rfq'` here, commented "no per-line route evidence on this wire".
 * That was wrong: `Quotation Line.source` carries exactly that — 'Provision' for a blanket-order
 * line, 'Base Price' for one priced from in-house stock, 'Supplier' for one a supplier quoted.
 * They are the values `contractQuotation.ts` and `quotationFromAllocation.ts` write, and measured
 * live on QTN-2026-00014 the field reads back verbatim.
 *
 * The cost of the hardcode was a contract-priced line wearing the blue `RFQ` badge (reported
 * 2026-09-05) — telling a purchaser a rate had been quoted by a supplier when it came from a
 * standing contract that skipped the RFQ entirely.
 *
 * 'Base Price' groups with 'Provision': both skipped the RFQ, which is what the badge distinguishes.
 * Anything unrecognised — including a line the server left blank — stays `'rfq'`, the safer read:
 * it claims no contract we cannot evidence.
 */
export function tagFromSource(source: unknown): 'provision' | 'rfq' {
  const value = str(source).trim().toLowerCase()
  return value === 'provision' || value === 'base price' ? 'provision' : 'rfq'
}


function toQuotationLine(w: WireQuotationItem, costCurrency: Currency, sellCurrency: Currency): QuotationLine {
  return {
    line_id: str(w.name),
    // No distinct customer serial on this wire — `enquiry_row_no` stands in, the same gap
    // `useComparison.ts`'s `toComparisonItem` documents for `get_comparison`.
    cust_sno: str(w.enquiry_row_no),
    item_code: str(w.item_code),
    tag: tagFromSource(w.source),
    source: '', // no server field
    qty: num(w.qty),
    uom: str(w.uom),
    sell_uom: null, // no server field for a chosen sell UOM
    cost_unit: money(w.cost_unit, costCurrency),
    // `category` omitted — see module header (Q15/B2).
    default_margin_pct: num(w.margin),
    sell_unit: money(w.sell_unit, sellCurrency),
    item_name: str(w.item_name),
    // Quotations built before 2026-10-02 carry our notes on the description; they never show.
    description: withoutInternalAnnotations(str(w.description)),
    offer_remarks: str(w.offer_remarks),
    /*
      The PO side. A document written before these were sent carries none of them, and for it
      the honest reading is "bought in the quotation's currency at the cost shown" — which is
      what `po_currency: <quotation>`, `po_currency_rate: cost`, `conversion_rate: 1` says.
    */
    po_currency: str(w.po_currency) || sellCurrency,
    po_currency_rate: num(w.po_currency_rate) > 0 ? num(w.po_currency_rate) : num(w.cost_unit),
    conversion_rate: num(w.conversion_rate) > 0 ? num(w.conversion_rate) : 1,
  }
}

export function toQuotation(w: WireQuotation): Quotation {
  const base = getBaseCurrency()
  /*
    `customer_currency` is on the `Quotation` doctype and IS what we send when building one, so
    when the method echoes it back it is the authoritative answer to "what is this quotation
    priced in".

    Falling back to `base` when it is absent is deliberate rather than lazy: the alternative —
    reading the customer's CURRENT default currency — would relabel every quotation written
    before this, and before the customer last changed currency, as though it had been priced that
    way. A quotation is a historical document; guessing its currency from today's master data is
    how a 12,000 AED quote comes to read 12,000 SAR.
  */
  const sellCurrency = (str(w.customer_currency) || base) as Currency
  /*
    ONE currency per line (corrected 2026-09-14). The cost used to be labelled `base` on the
    theory that it is "what we are charged" — but the server derives the sell from it
    (`sell_unit = cost_unit × (1 + margin/100)`, measured on QTN-2026-00017), so cost and sell are
    in the same currency by construction, and the writers now put the cost in the quotation's.
    Labelling it `base` printed a SAR figure as AED and a margin of neither.
  */
  const lines = (w.items ?? []).map((item) => toQuotationLine(item, sellCurrency, sellCurrency))
  const totals = previewTotals(lines, {
    /*
      ALL FOUR rates, not `management_commission` alone.

      The server charges `net × (management + software + port_agent + other_charges) / 100` and
      adds it to `grand_total` — measured live. Passing only management meant a quotation carrying
      any of the other three displayed a total BELOW what the customer would actually be charged.
    */
    commission_pct: totalCommissionPct({
      management: num(w.management_commission),
      software: num(w.software_commission),
      port_agent: num(w.port_agent_commission),
      other_charges: num(w.other_charges),
    }),
    convenience: money(w.convenience_amount, sellCurrency),
    markup_pct: num(w.markup),
  })

  return {
    id: str(w.name),
    status: toQuotationStatus(w.status),
    // IDENTITY, never the display label — see `Quotation.customer`'s own comment in ../types.
    // `customerName`-style "prefer the name, fall back to the code" logic (as `mappers/
    // opportunity.ts` uses for `Enquiry.customer`) is deliberately NOT applied here: this value
    // feeds `useQuotedItems`'s `/customers/{customer}/quoted-items` lookup, and a display label
    // standing in for a docname would silently query the wrong customer (or none) the moment
    // the two differ.
    customer: str(w.customer),
    customer_name: str(w.customer_name) || str(w.customer),
    grouped: false, // no server field
    revision: num(w.revision),
    lines,
    totals,
    fx: {}, // no FX snapshot on this wire — everything is already one currency
    currency: sellCurrency,
    // Company → quotation. `1` when the wire has none — and `1` whenever the quotation IS in the
    // company's currency, whatever is stored: an earlier re-price left `3.65` on an AED document
    // (QTN-2026-00037), and reading it scaled every AED figure by 3.65. A same-currency rate can
    // only ever be 1.
    exchange_rate: sellCurrency === base ? 1 : num(w.exchange_rate) > 0 ? num(w.exchange_rate) : 1,
    /*
      The commission panel, read off the document. `total_commission` is the SERVER's own figure —
      carried as-is rather than recomputed here, so the screen can show what is stored; the panel
      previews its own sum while an edit is in flight (`quotationCommission.ts`).
    */
    commission: {
      management: num(w.management_commission),
      software: num(w.software_commission),
      port_agent: num(w.port_agent_commission),
      other_charges: num(w.other_charges),
      total_commission: num(w.total_commission),
      /*
        `line_markup`, NOT `markup`. The two are different quantities that happen to share a name:
        `markup` is the document-level charge the server adds to the total, and this app pins it at
        0; `line_markup` is the rate the LINE prices were built with, which is what the panel shows
        and re-applies. Falls back to `markup` only for a quotation written before the field
        existed, so an old document still reports what it was priced at.
      */
      markup: num(w.line_markup) || num(w.markup),
      line_commission: num(w.line_commission),
    },
    discount: {
      // The doctype's Select allows exactly two values; anything else on the wire is not one of
      // them, and `Grand Total` is the ERP's own default.
      apply_on: (str(w.apply_discount_on) || str(w.apply_additional_discount_on)) === 'Net Total' ? 'Net Total' : 'Grand Total',
      apply_on_stored: str(w.apply_discount_on) === 'Net Total' || str(w.apply_discount_on) === 'Grand Total',
      pct: num(w.additional_discount_percentage),
      amount: num(w.additional_discount_amount),
    },
    total_po_cost: num(w.total_po_cost),
    base_discount_amount: num(w.base_discount_amount),
    // The server's own totals as stored — compared against ours by `lib/derivedFigures.ts`.
    stored_totals: {
      net_total: num(w.net_total),
      total: num(w.total),
      grand_total: num(w.grand_total),
      total_commission: num(w.total_commission),
      quotation_markup: num(w.quotation_markup),
      overall_margin: num(w.overall_margin),
      total_taxes_and_charges: num(w.total_taxes_and_charges),
      base_total: num(w.base_total),
      base_net_total: num(w.base_net_total),
      base_grand_total: num(w.base_grand_total),
      base_total_taxes_and_charges: num(w.base_total_taxes_and_charges),
    },
    taxes: (w.taxes ?? []).map(toTaxRow),
    tc_name: str(w.tc_name),
    terms: str(w.terms),
    payment_terms_template: str(w.payment_terms_template),
    customer_address: str(w.customer_address),
    sales_person: str(w.sales_person),
    purchaser: str(w.purchaser),
    customer_reference_no: str(w.customer_reference_no),
    company: str(w.company),
    enquiry_type: str(w.enquiry_type),
    delivery_time: str(w.delivey_time),
    order_type: str(w.order_type) || 'Sales',
    vessel: str(w.vessel),
    port: str(w.port),
    delivery_location: str(w.delivery_location),
    // Falls back to the company's currency: PO figures are what we PAY, which is denominated in
    // the base currency unless the document says otherwise.
    po_currency: str(w.po_currency) || base,
  }
}

/**
 * Load the enquiry's quotation. LOOKS IT UP — it does not build it.
 *
 * This used to call `comparison.build_quotation` first, as a get-or-create. That method cannot run:
 * `comparison.py:924` imports `quoflow.quoflow.quoflow.quoflow.api.quotation`, four segments where
 * the module has three. So the Customer Quotation screen could not open ANY quotation, including
 * ones that exist and are fully priced — QTN-2026-00004 sat at AED 210.45 while the page that
 * displays it failed on a call it did not need to make.
 *
 * Creating is now the comparison screen's job (`quotationFromAllocation.ts` → `create_quotation`),
 * which is where the allocation to price from actually is. This screen only reads, so "no quotation
 * yet" is a real state to report rather than something to fix by writing a document.
 */
async function loadQuotationForEnquiry(enquiryId: string): Promise<Quotation> {
  const name = await findQuotationName(enquiryId)
  if (name === null) {
    throw new Error(
      `No quotation for ${enquiryId} yet — allocate the suppliers on the comparison and build it from there.`,
    )
  }
  return readQuotation(name)
}

/** The enquiry's quotation docname, or `null`. One place, so every caller agrees on the answer. */
export async function findQuotationName(enquiryId: string): Promise<string | null> {
  const page = await getResource<{ name?: string }>('Quotation', {
    filters: [['enquiry', '=', enquiryId]],
    fields: ['name'],
    limit: 1,
  })
  return str(page.data[0]?.name) || null
}

/** `quotation.get_quotation` for a name already known to exist. Pure GET, no side effect. */
async function readQuotation(name: string): Promise<Quotation> {
  return toQuotation(await callGet<WireQuotation>('quotation', 'get_quotation', { name }))
}

/**
 * The enquiry's quotation. Throws when it has none — creating one is the comparison screen's job
 * (`quotationFromAllocation.ts` → `create_quotation`), which is where the allocation to price
 * from actually lives.
 *
 * Polls, so a status or total that moved server-side reaches an open `/quote` without a reload.
 * Safe to poll only because this read path no longer WRITES: it looks the name up and loads it,
 * and `comparison.build_quotation` is gone from it entirely.
 */
export function useQuotation(enquiryId: string | null | undefined) {
  return useQuery({
    queryKey: qk.quotationForEnquiry(enquiryId ?? ''),
    queryFn: () => loadQuotationForEnquiry(enquiryId as string),
    enabled: !!enquiryId,
    retry: false,
    // Poll only once a load has SUCCEEDED. If the enquiry has no quotation, `loadQuotationForEnquiry`
    // throws, and a query in that state must not retry on a 30-second timer forever.
    //
    // Keyed on `status`, deliberately NOT on `state.data` being truthy: `useExistingQuotation`
    // resolves to `null` for an enquiry that legitimately has no quotation yet, and a truthiness
    // check would then never start polling — so `/revisions` would never notice one being created.
    // A background refetch failure leaves `status` at 'success' with the last-good data, which is
    // the behaviour we want: keep polling, keep showing what we had.
    refetchInterval: (query) => (query.state.status === 'success' ? POLL_MS : false),
  })
}

/**
 * Read-only: what the enquiry HAS, reported as `null` rather than an error when there is none —
 * which, for `RevisionsPage`, is an expected condition and not a failure to put on screen.
 *
 * Polls on the same guard as `useQuotation`. Note that `null` here is a SUCCESSFUL load, so this
 * hook keeps polling for an enquiry with no quotation yet and will pick one up when the
 * comparison screen creates it.
 */
export function useExistingQuotation(enquiryId: string | null | undefined) {
  return useQuery({
    queryKey: qk.existingQuotationForEnquiry(enquiryId ?? ''),
    queryFn: async () => {
      // Calls `findQuotationName` directly rather than catching `loadQuotationForEnquiry`'s
      // throw. The two failure modes must not be conflated: the lookup RESOLVING to "no
      // quotation" (`name === null`) is a real, expected state on this screen and reported as
      // `null` — but a genuine thrown error (the lookup itself failing, e.g. a permission or
      // network error) must propagate so the query lands in `'error'` status, not `'success'`
      // with a `null` payload. Swallowing every error to `null` here used to make the
      // `refetchInterval` guard below poll a guaranteed-failing request forever, since `null` is
      // a resolved value and the guard only looks at `status`.
      const name = await findQuotationName(enquiryId as string)
      return name === null ? null : readQuotation(name)
    },
    enabled: !!enquiryId,
    retry: false,
    // Poll only once a load has SUCCEEDED. If the enquiry has no quotation, `loadQuotationForEnquiry`
    // throws, and a query in that state must not retry on a 30-second timer forever.
    //
    // Keyed on `status`, deliberately NOT on `state.data` being truthy: `useExistingQuotation`
    // resolves to `null` for an enquiry that legitimately has no quotation yet, and a truthiness
    // check would then never start polling — so `/revisions` would never notice one being created.
    // A background refetch failure leaves `status` at 'success' with the last-good data, which is
    // the behaviour we want: keep polling, keep showing what we had.
    refetchInterval: (query) => (query.state.status === 'success' ? POLL_MS : false),
  })
}

/* ============================================================================================
 * quotation.update_quotation_line (PATCH) → useUpdateQuotationLine
 * ============================================================================================ */

/**
 * Only `sell_unit` is exposed to the UI — `data` on the wire accepts exactly `{margin,
 * sell_unit}`, nothing else. There is no `qty` or `sell_uom` field here at all (both were
 * editable on the old `/api/v1` flow): a purchaser can no longer change a quotation line's
 * quantity or pick a preset sell UOM through this endpoint. `margin` is not exposed as a
 * SEPARATE input either — it is derived from whatever `sell_unit` the user typed
 * (`markupPct`, `lib/pricing.ts`) and sent alongside it, so the two numbers the server stores
 * never disagree with each other.
 */
export interface LinePatch {
  sell_unit: Money
}

/**
 * `sellForMargin` is the same price expressed in `cost`'s currency; `patch.sell_unit` is the
 * price as it will be STORED. They differ whenever the customer is billed in something other
 * than the company's currency — see `updateLineFromVars`.
 */
async function updateQuotationLine(
  quotationId: string,
  lineId: string,
  cost: Money,
  sellForMargin: Money,
  patch: LinePatch,
): Promise<void> {
  await patchQuotationLine(quotationId, lineId, {
    margin: markupPct(cost, sellForMargin),
    sell_unit: patch.sell_unit.amount,
  })
}

export interface UpdateLineVars {
  lineId: string
  /** The line's OWN cost — the caller's, never looked up from a query cache. See
   * `updateLineFromVars`'s own comment for why. */
  cost: Money
  /**
   * What the QUOTATION is priced in — the currency `sell_unit` is stored in, which is the
   * customer's and not necessarily the cost's. Optional: omitted, the cost's currency stands in,
   * which is right for the single-currency case and is what every caller meant before quotations
   * could be written in a customer's currency.
   */
  sellCurrency?: Currency
  fx: WireFx
  patch: LinePatch
}

/**
 * `cost`/`fx` come from the CALLER, not a query cache lookup — a fix-round correction
 * (task-11-report.md's fix log). The original version read `quotationForEnquiry(enquiryId)` out
 * of the `QueryClient`, which is exactly wrong from `RevisionsPage`: that screen's data lives
 * under the SEPARATE `existingQuotationForEnquiry` key (`keys.ts`'s own comment on why the two
 * must not share a cache entry), so the lookup silently missed, `cost` fell back to `{amount:
 * 0}`, and every reprice applied through Revisions PATCHed `margin: 0` next to a real
 * `sell_unit` — corrupting the stored customer-facing margin on the ONLY path that resolves a
 * "reprice" customer request. Requiring the caller to hand over the line's own `cost_unit`
 * (which every call site already has — it is reading from the very `QuotationLine` it is
 * editing) makes that class of bug structurally impossible: there is no cache key left to get
 * wrong. Extracted as its own pure function (rather than inlined in the `useMutation` call) so
 * it is directly testable without rendering the hook — this repo's convention (no
 * `@testing-library/react` in the tree).
 *
 * `QuoteRow` edits in the screen's DISPLAY currency, which is neither reliably the cost's nor
 * reliably the quotation's, so the typed amount is converted TWICE — into different currencies,
 * for different purposes:
 *
 *   - **stored** in `sellCurrency`, the currency the DOCUMENT is written in. A quotation built
 *     for a SAR customer holds SAR prices; PATCHing an AED number into that field silently
 *     restates the price (13.80 where 14.08 was meant — a 2% cut nobody typed).
 *   - **compared** in `cost.currency` for the margin only, because a percentage across two
 *     currencies' raw amounts is not a percentage of anything.
 *
 * Getting this wrong was harmless while `Quotation.fx` was always `{}` — every conversion was a
 * no-op, so both readings agreed by accident. Giving the screen a real FX snapshot is exactly
 * what ends that, which is why the two are separated here rather than left to coincide.
 */
function updateLineFromVars(quotationId: string, vars: UpdateLineVars): Promise<void> {
  const sellCurrency = vars.sellCurrency ?? vars.cost.currency
  const stored = convert(vars.patch.sell_unit, sellCurrency, vars.fx)
  const forMargin = convert(vars.patch.sell_unit, vars.cost.currency, vars.fx)
  return updateQuotationLine(quotationId, vars.lineId, vars.cost, forMargin, { sell_unit: stored })
}

export function useUpdateQuotationLine(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onError?: (error: BosunApiError) => void; onSuccess?: () => void },
) {
  rememberEnquiry(quotationId, enquiryId)
  const queryClient = useQueryClient()
  return useMutation<void, BosunApiError, UpdateLineVars>({
    /*
      Queued per quotation. Frappe rejects a write whose read predates the document's `modified`,
      and this screen writes from four places — the commission panel, each line, the taxes grid,
      and a field committing on blur as a button is clicked. Any two landing together produced
      "has been modified after you have opened it", milliseconds apart. See `serialWrites.ts`.
    */
    mutationFn: (vars) =>
      withDocumentLock(quotationId as string, () => updateLineFromVars(quotationId as string, vars)),
    onSuccess: () => {
      // The backend stores what it is sent rather than recomputing (this task's binding rule),
      // but a refetch still picks up exactly the line just written — simplest way to stay in
      // sync with the one copy of the data, matching the old file's own reasoning. Both keys:
      // `QuotePage` (`useQuotation`) and `RevisionsPage` (`useExistingQuotation`) may each hold
      // an independent cached copy of the same document (`keys.ts`'s own comment).
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      // Frappe writes a `Version` for every save, so the history panel is stale the moment this
      // lands — see `qk.quotationHistory`.
      if (quotationId) void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
      options?.onSuccess?.()
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * quotation.update_quotation_line (printed fields + PO currency) → useUpdateQuotationLineFields
 * ============================================================================================ */

/**
 * The two printed texts a line carries — separate from `LinePatch`, which is the sell price and
 * nothing else, on purpose: pricing goes through `updateLineFromVars`'s currency reasoning and
 * these do not.
 *
 * `description` and `offer_remarks` print for the customer (Akhil, item 8); both measured
 * writable through `update_quotation_line` 2026-09-14. Deliberately NOT the PO fields
 * (`po_currency`, `po_currency_rate`, `conversion_rate`, `cost_unit`): a line's currency is
 * decided on the comparison page (Akhil, same day), and nothing on the quotation page may move
 * it — the quotation reports the decision, it does not take it.
 */
export interface LineFieldsPatch {
  description?: string
  offer_remarks?: string
  /** The printed name — written onto a revision-added line, which `apply_revisions` leaves blank
   *  (D29). Measured writable 2026-09-15. */
  item_name?: string
  /**
   * The line's serial number — `enquiry_row_no` (the through-key the whole chain reads) and
   * `row_no` (what prints), written together. Measured writable 2026-09-15. Used for a
   * revision-added line, which arrives unnumbered, so it can take a removed line's place.
   */
  position?: string
}

async function updateQuotationLineFields(quotationId: string, lineId: string, patch: LineFieldsPatch): Promise<void> {
  const data: Record<string, string | number> = {}
  if (patch.description !== undefined) data.description = withoutInternalAnnotations(patch.description)
  if (patch.offer_remarks !== undefined) data.offer_remarks = patch.offer_remarks
  if (patch.item_name !== undefined) data.item_name = patch.item_name
  if (patch.position !== undefined) {
    data.enquiry_row_no = patch.position
    data.row_no = patch.position
  }
  if (Object.keys(data).length === 0) {
    throw new Error('updateQuotationLineFields called with nothing to write')
  }
  await patchQuotationLine(quotationId, lineId, data)
}

export function useUpdateQuotationLineFields(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onError?: (error: BosunApiError) => void; onSuccess?: () => void },
) {
  rememberEnquiry(quotationId, enquiryId)
  const queryClient = useQueryClient()
  return useMutation<void, BosunApiError, { lineId: string; patch: LineFieldsPatch }>({
    // Same per-document queue as every other write to this quotation — see `serialWrites.ts`.
    mutationFn: ({ lineId, patch }) =>
      withDocumentLock(quotationId as string, () => updateQuotationLineFields(quotationId as string, lineId, patch)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      if (quotationId) void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
      options?.onSuccess?.()
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * quotation.update_quotation_line (cost only) → useSetLineCosts
 * ============================================================================================ */

/**
 * Writes the supplier cost onto lines a revision apply just created.
 *
 * ---- Why only `cost_unit` ----
 *
 * Measured live 2026-09-04 on QTN-2026-00014: PATCHing `{cost_unit: 20}` alone came back
 * `cost_unit 20.0, margin 0.0, sell_unit 20.0, amount 20.0` — the server derives the sell price
 * and the amount itself. Sending our own `margin`/`sell_unit` would impose a markup the purchaser
 * never asked for, which is the very thing the 15% default (`D26`) is disliked for. The line
 * arrives priced at cost with zero margin, and the purchaser marks it up deliberately.
 *
 * ---- Serialized, and not merely for tidiness ----
 *
 * These land immediately after `apply_revisions` has itself written the document. Frappe rejects
 * a write whose read predates `modified`, so firing several in parallel reproduced
 * "has been modified after you have opened it" exactly as the line/commission/taxes writes did.
 * One `withDocumentLock` per call, awaited in turn — see `serialWrites.ts`.
 */
/** The PO side a revision add records — the supplier's own figure, in their currency. */
export interface LinePoSide {
  po_currency: string
  po_currency_rate: number
  /** `po_currency` → the quotation's currency, so `po_currency_rate × conversion_rate = cost`. */
  conversion_rate: number
}

/** Who a revision-added line is bought from — see `revisionBatch.ts`'s `PendingEdit.supplier`. */
export interface LineSupplierSide {
  supplier: string
  supplier_quotation?: string
}

async function setLineCost(
  quotationId: string,
  lineId: string,
  cost: number,
  sell?: number,
  source?: string,
  po?: LinePoSide,
  who?: LineSupplierSide,
): Promise<void> {
  /*
    `sell` is the price the quotation is already running at (`prevailingSellRatio`), sent with the
    margin it implies. Omitted, the server derives `sell_unit = cost_unit` — which quotes the new
    line AT COST beside every other line's markup. `margin` travels with it because the two are
    stored independently and a sell price without one reads as 0% margin in every later view.
  */
  // No cost fields at all when there is no cost (a supplier-only write): `cost_unit: 0` would
  // wipe the price the server found.
  const data: Record<string, number | string> =
    cost <= 0
      ? {}
      : sell === undefined
        ? { cost_unit: cost }
        : { cost_unit: cost, sell_unit: sell, margin: Math.round(((sell - cost) / cost) * 10000) / 100 }

  /*
    `source` records the ROUTE, and the badge reads it (`tagFromSource`). `apply_revisions` leaves
    it null on a line it creates, so a line priced from a blanket order showed as `RFQ` until this
    was sent. Measured writable 2026-09-05: PATCHing `source: 'Provision'` read back verbatim.
  */
  if (source) data.source = source as never
  /*
    The supplier's own figure and currency (Akhil, 2026-09-15). `cost` above is in the
    QUOTATION's currency and moves with every exchange change; this is what they actually quoted,
    and stays — the base price the purchaser wants to see beside it.
  */
  if (po) {
    data.po_currency = po.po_currency
    data.po_currency_rate = po.po_currency_rate
    data.conversion_rate = po.conversion_rate
  }
  /*
    The rest of what a line created from the comparison carries and a revision-added line did
    not (Akhil, 2026-09-16 — the PO is raised from these): the supplier and the quotation it was
    recorded on, the lowest-quote pair (one offer: it is the lowest), the selected rate, and the
    route tag. Every one accepted by `update_quotation_line` (probed on QTN-2026-00058, reverted).
  */
  if (who) {
    data.supplier = who.supplier
    data.lowest_quote_supplier = who.supplier
    if (who.supplier_quotation) data.supplier_quotation = who.supplier_quotation
  }
  if (cost > 0) {
    data.selected_rate = cost
    if (who) data.lowest_quote_price = cost
  }
  if (source) data.tag = source.trim().toLowerCase() === 'supplier' ? 'rfq' : 'provision'

  await patchQuotationLine(quotationId, lineId, data)
}

export function useSetLineCosts(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onError?: (error: BosunApiError) => void },
) {
  rememberEnquiry(quotationId, enquiryId)
  const queryClient = useQueryClient()
  return useMutation<void, BosunApiError, { line_id: string; cost: number; sell?: number; source?: string; po?: LinePoSide; supplier?: string; supplier_quotation?: string }[]>({
    mutationFn: async (rows) => {
      for (const row of rows) {
        await withDocumentLock(quotationId as string, () =>
          setLineCost(
            quotationId as string,
            row.line_id,
            row.cost,
            row.sell,
            row.source,
            row.po,
            row.supplier ? { supplier: row.supplier, ...(row.supplier_quotation ? { supplier_quotation: row.supplier_quotation } : {}) } : undefined,
          ),
        )
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      if (quotationId) void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * quotation.update_quotation (terms only) → useUpdateQuotationTerms
 * ============================================================================================ */

/**
 * The three per-quotation terms. All three are live-writable — measured 2026-08-09 on
 * QTN-2026-00004, which moved grand_total 199.25 → 206.75 → 216.7125 and recomputed
 * `total_commission` and `overall_margin` server-side.
 *
 * They are NOT company settings, whatever `TotalsPanel` used to claim: they live on the
 * `Quotation` doctype and `settings.get_pricing_rules` returns none of them.
 */
export interface QuotationTerms {
  markup?: number
  /** What the LINE prices were built with — inert storage, added 2026-09-07. See `WireQuotation`. */
  line_markup?: number
  line_commission?: number
  management_commission?: number
  convenience_amount?: number
  /* ---- The rest of the commission panel (Akhil's ERP screen, 2026-09-04) ----
   *
   * All real `Percent` fields on the live `Quotation`; this app previously wrote only
   * `management_commission` of the four, so three of the numbers on that panel had nowhere to go.
   * `total_commission` is deliberately NOT here — the server computes it from these. */
  software_commission?: number
  port_agent_commission?: number
  other_charges?: number

  /* ---- Additional discount ----
   *
   * `apply_discount_on` is a Select of exactly these two values on the doctype; sending
   * anything else would fail validation. Percentage and amount are one figure two ways — see
   * `lib/additionalDiscount.ts` — and only the one the purchaser typed is sent, so the server's own
   * derivation of the other is never overwritten with our rounding of it. */
  apply_discount_on?: 'Grand Total' | 'Net Total'
  additional_discount_percentage?: number
  additional_discount_amount?: number
  /**
   * The whole `taxes` child table, replaced WHOLESALE — the same rule `items` follows. Send every
   * row you intend to keep; omit the key entirely to leave the table alone.
   */
  /** The chosen `Terms and Conditions` docname, or `''` to clear it. */
  tc_name?: string
  /** The `Payment Terms Template` docname (`lookups.get_payment_terms`), or `''` to clear it. */
  payment_terms_template?: string
  /**
   * The address TEXT the quotation is addressed to. `Quotation.customer_address` is a Small Text
   * (checked against the doctype 2026-09-16), not an `Address` Link — the docname this used to
   * carry printed as a docname. See `QuotationHeaderCard`.
   */
  customer_address?: string
  /* The header the quotation card edits (Akhil, 2026-09-16). `customer` is the Link identity. */
  customer?: string
  sales_person?: string
  purchaser?: string
  customer_reference_no?: string
  /** Written as `delivey_time` — the doctype's spelling. */
  delivery_time?: string
  /** The quotation's order type — one of the doctype's Select values. */
  order_type?: string
  /** The quotation's own enquiry type (Link) — written with the enquiry's when it changes. */
  enquiry_type?: string
  /**
   * Logistics (Akhil, 2026-09-15): editable on the quotation, and written here by the RFQ header
   * card too when the enquiry's change, so the two documents cannot disagree. Docnames — a
   * display name fails Link validation.
   */
  vessel?: string
  port?: string
  delivery_location?: string
  /** The terms HTML. Sent separately from `tc_name` — a purchaser may edit the text of a template
   *  without changing which template it came from, and the two must not overwrite each other. */
  terms?: string
  taxes?: Array<{
    charge_type: string
    account_head: string
    tax_rate: number
    tax_amount: number
    row_id: string
    /** ERPNext's after-discount amount for the row — written with the amount (`lib/derivedFigures.ts`). */
    tax_amount_after_discount_amount?: number
    /** Running total after the row, on the after-discount figures (ERPNext's `total`). */
    total?: number
  }>
  /* Derived VALUES the server does not compute — every one the panel shows, `lib/derivedFigures.ts`. */
  base_discount_amount?: number
  total_po_cost?: number
  net_total?: number
  total?: number
  grand_total?: number
  total_commission?: number
  quotation_markup?: number
  overall_margin?: number
  total_taxes_and_charges?: number
  /* The company-currency twins and ERPNext's quotation → company multiplier (2026-09-17). */
  base_total?: number
  base_net_total?: number
  base_grand_total?: number
  base_total_taxes_and_charges?: number
}

/**
 * PUTs ONLY the named terms.
 *
 * `update_quotation` replaces `items` wholesale, so this must never include that key — editing a
 * convenience fee has no business touching the lines. Throwing on an empty patch rather than
 * PUTting `{}` keeps a no-op edit from looking like a successful save.
 */
async function updateQuotationTerms(quotationId: string, terms: QuotationTerms): Promise<void> {
  const data = termsToWire(terms)
  if (Object.keys(data).length === 0) {
    throw new Error('updateQuotationTerms called with no terms — refusing to PUT an empty patch')
  }
  await putQuotation(quotationId, data)
}

/**
 * Which enquiry a quotation belongs to — registered by every hook that writes to one (they all
 * take both ids), read by the healing writes below so the five write primitives keep their
 * signatures. A quotation belongs to exactly one enquiry, so the map cannot disagree.
 */
const enquiryOfQuotation = new Map<string, string>()
const rememberEnquiry = (quotationId: string | undefined, enquiryId: string) => {
  if (quotationId) enquiryOfQuotation.set(quotationId, enquiryId)
}

/* ============================================================================================
 * Writes that survive the doctype's mandatory header (2026-09-17) — see `lib/quotationMandatory.ts`
 * ============================================================================================ */

/**
 * `update_quotation`, retried ONCE with the mandatory fields the server named, filled from the
 * enquiry. A quotation created before the doctype turned strict refuses every write until its
 * header is complete, and the header card could not complete it one field at a time — each
 * write failed for the others. The enquiry is where those values live, so they are copied in
 * with the write that needed them. What the enquiry lacks too is reported by name.
 */
async function putQuotation(quotationId: string, data: Record<string, unknown>): Promise<void> {
  // The server names the missing fields either all at once or ONE per failure ("Value missing
  // for Quotation: Company"), so the retry accumulates until the write goes or nothing more can
  // be filled — bounded by the number of mandatory fields.
  let extra: Record<string, string> = {}
  for (let attempt = 0; attempt <= QUOTATION_MANDATORY.length; attempt += 1) {
    try {
      await callPut('quotation', 'update_quotation', { name: quotationId, data: { ...extra, ...data } })
      return
    } catch (error) {
      const healed = await healMandatory(error, enquiryOfQuotation.get(quotationId) ?? null, extra)
      if (!healed) throw error
      extra = { ...extra, ...healed }
    }
  }
}

/** `update_quotation_line`, healed the same way — a line PATCH saves the parent and validates it. */
async function patchQuotationLine(quotationId: string, lineId: string, data: Record<string, unknown>): Promise<void> {
  try {
    await callPatch('quotation', 'update_quotation_line', { quotation: quotationId, line_id: lineId, data })
  } catch (error) {
    const healed = await healMandatory(error, enquiryOfQuotation.get(quotationId) ?? null, {})
    if (!healed) throw error
    // The parent's header first, through the healing PUT; then the line write again.
    await putQuotation(quotationId, healed)
    await callPatch('quotation', 'update_quotation_line', { quotation: quotationId, line_id: lineId, data })
  }
}

/** The fields to add for a retry, or `null` when this is not a MandatoryError we can answer. */
async function healMandatory(error: unknown, enquiryId: string | null, already: Record<string, string>): Promise<Record<string, string> | null> {
  const message = error instanceof Error ? error.message : String(error)
  const fields = mandatoryFieldsFrom(message).filter((f) => !(f in already))
  // A field named twice means the enquiry's value did not satisfy the server either — stop.
  if (fields.length === 0 || !enquiryId) return null
  const enquiry = await callGet<EnquiryHeaderSource & { port?: string | null }>('enquiry', 'get_enquiry', { name: enquiryId })
  const { filled, stillMissing } = fillFromEnquiry(fields, { ...enquiry, port_of_delivery: enquiry.port_of_delivery ?? enquiry.port ?? null })
  if (stillMissing.length > 0) {
    throw new Error(
      `The server now requires ${describeMandatory(fields)} on every quotation, and ${enquiryId} has no ${describeMandatory(stillMissing)}. Set ${stillMissing.length === 1 ? 'it' : 'them'} on the enquiry header (RFQ builder), then try again.`,
    )
  }
  return filled
}

/** The `update_quotation` body for a terms patch — only the keys named. Shared with the duplicate flow. */
export function termsToWire(terms: QuotationTerms): Record<string, unknown> {
  const data: Record<string, number | string> = {}
  if (terms.markup !== undefined) data.markup = terms.markup
  /*
    The two INERT fields — what the line prices were built with, recorded so they survive a reload.
    Kept apart from `markup`/`*_commission` above, which are the document-level charge (`D27`) and
    which this app pins at 0.
  */
  if (terms.line_markup !== undefined) data.line_markup = terms.line_markup
  if (terms.line_commission !== undefined) data.line_commission = terms.line_commission
  if (terms.management_commission !== undefined) data.management_commission = terms.management_commission
  if (terms.convenience_amount !== undefined) data.convenience_amount = terms.convenience_amount
  if (terms.software_commission !== undefined) data.software_commission = terms.software_commission
  if (terms.port_agent_commission !== undefined) data.port_agent_commission = terms.port_agent_commission
  if (terms.other_charges !== undefined) data.other_charges = terms.other_charges
  if (terms.apply_discount_on !== undefined) {
    data.apply_discount_on = terms.apply_discount_on
  }
  if (terms.additional_discount_percentage !== undefined) {
    data.additional_discount_percentage = terms.additional_discount_percentage
  }
  if (terms.additional_discount_amount !== undefined) {
    data.additional_discount_amount = terms.additional_discount_amount
  }
  if (terms.base_discount_amount !== undefined) data.base_discount_amount = terms.base_discount_amount
  if (terms.total_po_cost !== undefined) data.total_po_cost = terms.total_po_cost
  for (const key of ['net_total', 'total', 'grand_total', 'total_commission', 'quotation_markup', 'overall_margin', 'total_taxes_and_charges', 'base_total', 'base_net_total', 'base_grand_total', 'base_total_taxes_and_charges'] as const) {
    if (terms[key] !== undefined) data[key] = terms[key] as number
  }
  if (terms.tc_name !== undefined) data.tc_name = terms.tc_name
  if (terms.terms !== undefined) data.terms = terms.terms
  if (terms.payment_terms_template !== undefined) data.payment_terms_template = terms.payment_terms_template
  if (terms.customer_address !== undefined) data.customer_address = terms.customer_address
  if (terms.customer !== undefined) data.customer = terms.customer
  if (terms.sales_person !== undefined) data.sales_person = terms.sales_person
  if (terms.purchaser !== undefined) data.purchaser = terms.purchaser
  if (terms.customer_reference_no !== undefined) data.customer_reference_no = terms.customer_reference_no
  if (terms.delivery_time !== undefined) data.delivey_time = terms.delivery_time
  if (terms.order_type !== undefined) data.order_type = terms.order_type
  if (terms.enquiry_type !== undefined) data.enquiry_type = terms.enquiry_type
  if (terms.vessel !== undefined) data.vessel = terms.vessel
  if (terms.port !== undefined) data.port = terms.port
  if (terms.delivery_location !== undefined) data.delivery_location = terms.delivery_location
  if (terms.taxes !== undefined) {
    // Wholesale, like `items`. Included only when the caller actually names it, so editing a
    // commission rate never touches the tax table.
    ;(data as Record<string, unknown>).taxes = terms.taxes
  }
  return data
}

/**
 * The server recomputes `grand_total` from these, so a successful write invalidates the
 * quotation rather than patching a total in the cache — the authoritative number is the one it
 * returns, not the one we previewed.
 */
/* ============================================================================================
 * quotation.update_quotation (currency) → useApplyQuotationCurrency
 * ============================================================================================ */

/**
 * Re-price the whole quotation into another currency.
 *
 * Deliberately NOT what the currency picker does — that is a view. This is the act of changing
 * what the customer is billed in, so it rewrites every selling price and is only ever triggered
 * by an explicit confirmation.
 *
 * ONE request, by design. The alternative — a `update_quotation_line` PATCH per line, then a PUT
 * for the scalars — is not atomic, and a failure halfway through leaves a quotation with some
 * lines converted and some not, which is worse than not converting at all.
 *
 * The RAW rows are read back from `get_quotation` and echoed with only the prices replaced,
 * because `update_quotation` replaces `items` wholesale and the domain line drops fields the
 * server holds. See `lib/quotationCurrency.ts`.
 */
/**
 * TWO rates, and they are different things (ENQ-2026-00146, 2026-09-16):
 *
 *   step             the quotation's CURRENT currency → `currency`: what every line is multiplied by
 *   companyToTarget  the COMPANY's currency → `currency`: what `Quotation.exchange_rate` MEANS,
 *                    and `1` when they are the same
 *
 * This used to store `step` as `exchange_rate`. Converting USD → AED at an AED company then left
 * `exchange_rate: 3.65` on an AED document, and everything reading the field as company →
 * quotation scaled the AED figures by 3.65 again — 4,323.77 became 15,782.
 */
export interface RepriceRates {
  step: number
  companyToTarget: number
}

async function applyQuotationCurrency(quotationId: string, currency: string, rates: RepriceRates): Promise<void> {
  const { step, companyToTarget } = rates
  if (!Number.isFinite(step) || step <= 0 || !Number.isFinite(companyToTarget) || companyToTarget <= 0) {
    throw new Error(`Refusing to re-price ${quotationId} at a rate of ${step} (company rate ${companyToTarget})`)
  }

  const current = await callGet<WireQuotation & { items?: WireItemRow[] }>('quotation', 'get_quotation', {
    name: quotationId,
  })
  // A line with no PO side yet is pinned to the currency it is LEAVING, so its base price stays
  // labelled as what it was — then every line, and every fixed amount, moves by the step.
  const from = str(current.customer_currency) || getBaseCurrency()
  const items = convertItems(pinPoSide(current.items ?? [], from), step)

  await putQuotation(quotationId, {
    items,
    customer_currency: currency,
    exchange_rate: companyToTarget,
    net_total: convertedNetTotal(items),
    // The delivery charge and the discount AMOUNT are money too (Akhil, 2026-09-16) — left as
    // they were, they were read in the new currency at the old figure. See `convertFixedAmounts`.
    ...convertFixedAmounts(current, step),
  })
}

export function useApplyQuotationCurrency(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onSuccess?: () => void; onError?: (error: BosunApiError) => void },
) {
  rememberEnquiry(quotationId, enquiryId)
  const queryClient = useQueryClient()
  return useMutation<void, BosunApiError, { currency: string; rates: RepriceRates }>({
    // Re-pricing reads the document and writes it back, so it must not overlap a line edit.
    mutationFn: ({ currency, rates }) =>
      withDocumentLock(quotationId as string, () => applyQuotationCurrency(quotationId as string, currency, rates)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      if (quotationId) void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
      options?.onSuccess?.()
    },
    onError: (error) => options?.onError?.(error),
  })
}

export function useUpdateQuotationTerms(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onSuccess?: () => void; onError?: (error: BosunApiError) => void },
) {
  rememberEnquiry(quotationId, enquiryId)
  const queryClient = useQueryClient()
  return useMutation<void, BosunApiError, QuotationTerms>({
    // Same queue as the line writes — the collision this fixes is BETWEEN the two, not within
    // either. See `useUpdateQuotationLine`.
    mutationFn: (terms) =>
      withDocumentLock(quotationId as string, () => updateQuotationTerms(quotationId as string, terms)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      if (quotationId) void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
      options?.onSuccess?.()
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * quotation.send_quotation → useSendQuotation
 * ============================================================================================ */

interface WireViolation {
  line_id?: string
  item_code?: string
  reason?: string
  message?: string
}

interface WireSendQuotationResult {
  status: string
  sent_at: string
  violations: WireViolation[]
}

/** The brief documents `violations: []` without an item shape; this reads every plausible field
 * defensively rather than assuming one, so a violation is never silently dropped for using the
 * "wrong" key name. */
function violationMessage(w: WireViolation): string {
  const reason = str(w.reason) || str(w.message) || 'Pricing-rule violation'
  const target = str(w.line_id) || str(w.item_code)
  return target ? `${target}: ${reason}` : reason
}

export interface SendQuotationInput {
  to?: string[]
  /** The purchaser — on CC of every outgoing mail (Akhil, 2026-09-17). */
  cc?: string[]
  subject?: string
  note?: string
  /**
   * The logged-in user, so the customer sees who sent it rather than the mail account's own name.
   *
   * ---- Sent, and currently IGNORED by the server ----
   *
   * Measured 2026-09-06, three ways: the bare address, the `"Name <addr>"` form the RFQ sender
   * accepts, and six alternative key names in one call. Every one returned 200 and left the queue
   * row reading `Rfqai <rfqai@lynx.sa>`. `send_quotation` has no sender parameter.
   *
   * It is sent anyway because the capability exists one endpoint away — `rfq_email_with_excel`
   * takes a `sender` and honours it, which is why four RFQ mails on this bench read
   * `AKHIL Nair <rfqai@lynx.sa>` while every quotation reads `Rfqai`. When `send_quotation` grows
   * the same parameter this works with no further change here.
   *
   * Only the display NAME can ever be the user's: `mail.lynx.sa` authenticates one account, so the
   * address stays the company's in every personalised RFQ mail too.
   */
  sender?: string
}

/**
 * `violations[]` is the AUTHORITATIVE gate (this task's binding rule) — a non-empty array BLOCKS
 * the send. Thrown as a `BosunApiError(409, …)` carrying every violation's message in
 * `.messages`, so it lands on the exact code path `QuotePage`/`RevisionsPage` already special-
 * case for 409 (their onError already reads "pricing-rule violations" out of a 409 today; this
 * task tightens that from a courtesy client-side check into what the server actually enforces).
 *
 * FAILS CLOSED (fix round, task-11-report.md): the item shape inside `violations[]` is a guess
 * (the brief only documents `violations: []`) — that made the guess load-bearing rather than
 * contained, since `w.violations ?? []` read ANY unrecognised response shape (the key renamed,
 * the field omitted, a non-array) as "clean" and let the send through. Three checks now, each
 * blocking rather than defaulting open: `violations` must actually BE an array; a non-empty one
 * blocks with every message; and an empty one still requires a real `sent_at` — the mock itself
 * returns `sent_at: null` on the blocked path, so treating a falsy `sent_at` as "sent" would have
 * reported success on exactly the response this endpoint uses to say no.
 */
async function sendQuotation(quotationId: string, input: SendQuotationInput): Promise<{ status: string; sent_at: string }> {
  /*
    REFUSES an empty recipient list, rather than letting the server decide.

    `send_quotation` does `to or [<broken lookup>]`, and an empty list is FALSY in Python — so
    `to: []` (what both call sites used to send) fell into a fallback that dies on
    `Unknown column 'customer_primary_contact'`. Every send and every re-issue failed that way,
    and the error named a database column, which tells the user nothing they can act on.

    Resolving the address is the caller's job now (`useCustomerContacts.ts`). This guard is here
    so no future call site can reintroduce the empty list — the failure it produces is remote,
    delayed and unreadable, which is exactly the kind that survives review.
  */
  const to = (input.to ?? []).map((address) => address.trim()).filter((address) => address !== '')
  if (to.length === 0) {
    throw new BosunApiError(
      400,
      'ValidationError',
      ['No email address on file for this customer — add a contact before sending.'],
      'Nobody to send this quotation to',
    )
  }

  const w = await callPost<WireSendQuotationResult>('quotation', 'send_quotation', {
    quotation: quotationId,
    to,
    // The purchaser on CC of every outgoing mail (Akhil, 2026-09-17). Sent whether or not
    // `send_quotation` honours it yet — `send_rfq` does; this one is not measured (`E20`).
    ...(input.cc && input.cc.length > 0 ? { cc: input.cc } : {}),
    subject: input.subject ?? '',
    note: input.note ?? '',
    // Omitted rather than sent empty — see `SendQuotationInput.sender` for what the server
    // currently does with it, and why it is sent regardless.
    ...(input.sender && input.sender.trim() !== '' ? { sender: input.sender.trim() } : {}),
  })

  if (!Array.isArray(w.violations)) {
    throw new BosunApiError(
      409,
      'ValidationError',
      ['send_quotation returned no violations[] — cannot confirm the send is clean'],
      'Unrecognised send_quotation response',
    )
  }
  if (w.violations.length > 0) {
    throw new BosunApiError(409, 'ValidationError', w.violations.map(violationMessage), 'Pricing-rule violations block this send')
  }

  const sentAt = str(w.sent_at)
  if (!sentAt) {
    throw new BosunApiError(
      409,
      'ValidationError',
      ['send_quotation reported no violations but also no sent_at — treating as not sent'],
      'Ambiguous send_quotation response',
    )
  }
  return { status: str(w.status), sent_at: sentAt }
}

/**
 * Marks a quotation SENT without mailing it — the sales team mailed it from Outlook (Akhil,
 * 2026-09-18: "ask the user if the mail was sent separately; if yes the status changes"). Writes
 * the same state `send_quotation` leaves (`markSentPatch`), then moves the enquiry, and
 * invalidates exactly what a real send invalidates so every page reads it the same way.
 */
export function useMarkQuotationSent(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onSuccess?: () => void; onError?: (error: BosunApiError) => void },
) {
  const queryClient = useQueryClient()
  rememberEnquiry(quotationId, enquiryId)
  return useMutation<void, BosunApiError, { by: string | null; currentStatus: EnquiryStatus }>({
    mutationFn: async ({ by, currentStatus }) => {
      if (!quotationId) throw new Error('No quotation to mark as sent')
      const patch = markSentPatch(new Date(), by)
      await withDocumentLock(quotationId, () => putQuotation(quotationId, patch.quotation))
      if (shouldAdvanceTo(currentStatus, patch.enquiryStatus)) {
        await callPut('enquiry', 'update_enquiry', { name: enquiryId, data: buildUpdateEnquiryWireBody({ status: patch.enquiryStatus }) })
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.enquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      if (quotationId) {
        void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
        void queryClient.invalidateQueries({ queryKey: qk.quotationSends(quotationId) })
      }
      options?.onSuccess?.()
    },
    onError: (error) => options?.onError?.(error),
  })
}

/**
 * KNOWN GAP: `to`/`subject`/`note` have no compose UI on either screen that calls this
 * (`QuotePage`'s "Send quotation to customer" button, `RevisionsPage`'s "Re-issue") — neither
 * the `Quotation` nor `Enquiry` domain type carries a customer email address to default `to`
 * from. Callers that don't pass a `SendQuotationInput` send empty strings/an empty list;
 * flagged in task-11-report.md rather than fabricated.
 */
export function useSendQuotation(
  enquiryId: string,
  quotationId: string | undefined,
  options?: { onSuccess?: (data: { status: string; sent_at: string }) => void; onError?: (error: BosunApiError) => void },
) {
  rememberEnquiry(quotationId, enquiryId)
  const queryClient = useQueryClient()
  return useMutation<{ status: string; sent_at: string }, BosunApiError, SendQuotationInput | void>({
    mutationFn: (input) => sendQuotation(quotationId as string, input ?? {}),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.enquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      // Sending flips `status`, and `/revisions` reads the quotation through the `existing` key.
      // The SENDS list is the one that matters most here: a send that does not appear in "sent to
      // the customer" reads as never having happened.
      if (quotationId) {
        void queryClient.invalidateQueries({ queryKey: qk.quotationHistory(quotationId) })
        void queryClient.invalidateQueries({ queryKey: qk.quotationSends(quotationId) })
      }
      options?.onSuccess?.(data)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * quotation.decide_quotation → useDecideQuotation
 * ============================================================================================ */

interface WireDecideResult {
  quotation?: string
  status?: string
}

/**
 * `status` on the wire is the doctype's RAW vocabulary (`Ordered`/`Cancelled`, same as
 * `get_quotation` — fix round, task-11-report.md: this used to be a bare `str(w.status) as
 * EnquiryStatus` cast, so a successful decide toasted "marked Ordered" instead of "marked Won").
 * Routed through the SAME `toQuotationStatus` `get_quotation` uses, which maps `Ordered`/
 * `Cancelled` onto exactly `'Won'`/`'Rejected'` — the two values `Quotation['status']` and
 * `EnquiryStatus` share verbatim. Falls back to the originally REQUESTED decision (never the raw
 * server word) if the response is something else entirely — the value the caller explicitly
 * asked for, not a guess.
 */
/**
 * Why a quotation was lost (Akhil, 2026-09-21) — the doctype's `lost_reasons` and `competitors`
 * (both Table MultiSelect, from the `masters.get_lost_reasons` / `get_competitor` lists) and
 * `order_lost_reason` (free text), all carried on `decide_quotation` and synced to G6.
 */
export interface LostDetails {
  lost_reasons: string[]
  competitors: string[]
  order_lost_reason: string
}

async function decideQuotation(quotationId: string, decision: 'Won' | 'Rejected', lost?: LostDetails): Promise<DecideResponse> {
  const body = {
    quotation: quotationId,
    decision,
    ...(decision === 'Rejected' && lost
      ? {
          lost_reasons: lost.lost_reasons,
          competitors: lost.competitors,
          ...(lost.order_lost_reason.trim() ? { order_lost_reason: lost.order_lost_reason.trim() } : {}),
        }
      : {}),
  }
  let w: WireDecideResult
  try {
    w = await callPost<WireDecideResult>('quotation', 'decide_quotation', body)
  } catch (error) {
    /*
      `decide_quotation` SAVES the document, so a quotation made before the header turned strict
      fails ERPNext's own validation first — "Company is required to fetch default warehouse"
      (QTN-2026-00014, 2026-09-21; 53 of the 80 open quotations that day). Healed like every other
      write: the header filled from the enquiry through `putQuotation`, then the decision again.
    */
    const healed = await healMandatory(error, enquiryOfQuotation.get(quotationId) ?? null, {})
    if (!healed) throw error
    await putQuotation(quotationId, healed)
    w = await callPost<WireDecideResult>('quotation', 'decide_quotation', body)
  }
  const mapped = toQuotationStatus(w.status)
  const status: EnquiryStatus = mapped === 'Won' || mapped === 'Rejected' ? mapped : decision
  return { id: str(w.quotation) || quotationId, status }
}

/**
 * Mark a sent quotation Won or Rejected (contract §9) — the board's only manual transition.
 * Purchaser/manager only and only from `Quoted`; both enforced server-side (403 / 409).
 */
export interface DecideVars {
  quotationId: string
  /** Carried for the confirmation message; not sent. */
  enquiryId: string
  to: 'Won' | 'Rejected'
  /** Rejection only — see `LostDetails`. */
  lost?: LostDetails
}

export function useDecideQuotation(options?: {
  onSuccess?: (data: DecideResponse, vars: DecideVars) => void
  onError?: (error: BosunApiError) => void
}) {
  const queryClient = useQueryClient()
  return useMutation<DecideResponse, BosunApiError, DecideVars>({
    mutationFn: async ({ quotationId, enquiryId, to, lost }) => {
      rememberEnquiry(quotationId, enquiryId)
      const decided = await decideQuotation(quotationId, to, lost)
      // A Won pushes the whole chain to G6 (Akhil, 2026-10-16) — after the decision is saved,
      // and never able to undo it: a failed push is a warning on the result, not an error.
      if (to === 'Won' && decided.status === 'Won') {
        const warning = await pushEnquiryChain(enquiryId)
        if (warning) decided.push_warning = warning
      }
      return decided
    },
    onSuccess: (data, vars) => {
      // The decision cascades to the enquiry, so the board and any open flow both move.
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(vars.enquiryId) })
      options?.onSuccess?.(data, vars)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * customers/{customer}/quoted-items — STILL ON THE OLD TRANSPORT
 * ============================================================================================
 *
 * No `quoflow.*` equivalent is in this task's verified live shapes ("what this customer has
 * been quoted before" — purchase history scoped to a customer). Rather than fabricate an
 * endpoint, this one function keeps calling the old `/api/v1` client (`./client`) until a real
 * one exists. Flagged explicitly in task-11-report.md's "still on the old transport" list for
 * Task 12 to route around when `client.ts`/`frappe.ts` are deleted.
 *
 * A8 (docs/bosun-required-api-round-2.md) — the specific ask that unblocks this hook. Not
 * proxied live (`vite.config.ts` routes `/api` wholesale now); `QuotePage.tsx` passes `null` for
 * `customer` instead of calling this while `lib/api/liveAvailability.ts` reports it unavailable,
 * so the hook's own `enabled: !!customer` gate keeps it from firing. Kept as-is, unguarded, so
 * it's ready to call the moment the endpoint exists.
 */
export function useQuotedItems(customer: string | null | undefined) {
  return useQuery({
    queryKey: qk.quotedItems(customer ?? ''),
    queryFn: () => legacyApi.get<QuotedItem[]>(`/customers/${encodeURIComponent(customer as string)}/quoted-items`),
    enabled: !!customer,
  })
}

/* ---------- test-only export ---------- */

export const __test__ = {
  rememberEnquiry,
  setLineCost,
  applyQuotationCurrency,
  loadQuotationForEnquiry,
  updateQuotationLine,
  updateLineFromVars,
  updateQuotationTerms,
  updateQuotationLineFields,
  sendQuotation,
  decideQuotation,
  repriceQuotation,
  createOrRepriceQuotation,
}

/**
 * The quotation already on this enquiry, or `null` — a REAL check.
 *
 * Carries `status` and `sent_at` as well as the name, because "does one exist" was never the
 * question that mattered — "has the customer seen it" is. A draft is re-priced from a new
 * allocation; a SENT quotation is never rewritten under the customer.
 */
export interface ExistingQuotation {
  name: string
  status: string
  /** Set once it has gone to the customer. A sent quotation is never rewritten in place. */
  sent_at: string | null
}

export function useQuotationNameForEnquiry(enquiryId: string | null | undefined) {
  return useQuery({
    queryKey: ['quotations', 'for-enquiry', enquiryId ?? ''] as const,
    queryFn: async (): Promise<ExistingQuotation | null> => {
      // Same doctype lookup `loadQuotationForEnquiry` uses — one definition of "does one exist",
      // asked with the extra fields this caller needs.
      const page = await getResource<{ name?: string; status?: string; sent_at?: string | null }>('Quotation', {
        filters: [['enquiry', '=', enquiryId ?? '']],
        fields: ['name', 'status', 'sent_at'],
        limit: 1,
      })
      const row = page.data[0]
      if (!row || str(row.name) === '') return null
      return { name: str(row.name), status: str(row.status), sent_at: str(row.sent_at) || null }
    },
    enabled: !!enquiryId,
  })
}

/**
 * Every enquiry's quotation number, in ONE read — for the requirements board (2026-09-15).
 *
 * The board is searched by quotation number as often as by enquiry number, and `get_enquiries`
 * carries none; asking `useQuotationNameForEnquiry` per card would be a request per enquiry
 * across hundreds. One `Quotation(name, enquiry)` list, newest first, gives the latest per
 * enquiry. Polled at the board's own cadence by the caller's `refetchInterval`.
 */
export function useQuotationsByEnquiry(options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: ['quotations', 'by-enquiry'] as const,
    queryFn: async (): Promise<Map<string, string>> => {
      const page = await getResource<{ name?: string; enquiry?: string | null }>('Quotation', {
        fields: ['name', 'enquiry'],
        orderBy: 'modified desc',
        limit: 2000,
      })
      const out = new Map<string, string>()
      for (const row of page.data) {
        const enquiry = str(row.enquiry)
        const name = str(row.name)
        // Newest first, so the first seen per enquiry is the current one.
        if (enquiry !== '' && name !== '' && !out.has(enquiry)) out.set(enquiry, name)
      }
      return out
    },
    staleTime: 60_000,
    refetchInterval: options?.refetchInterval,
  })
}

/* ============================================================================================
 * quotation.create_quotation — the client-built quotation
 * ============================================================================================ */

/**
 * Create the customer quotation from a payload the CLIENT priced.
 *
 * Used instead of `comparison.build_quotation`, which cannot run: it imports
 * `quoflow.quoflow.quoflow.quoflow.api.quotation` — four segments where the module has three, the
 * same defect as `B1`/`B2` in a third place (`comparison.py:924`). `create_quotation` is healthy;
 * probed with a bogus enquiry it answers `LinkValidationError`, i.e. it reaches its own validation.
 *
 * See `lib/quotationFromAllocation.ts` for what building the payload here does and does not claim.
 */
/**
 * `createQuotation`'s RE-PRICE branch — pulled out of the mutation so it is independently
 * testable (`__test__` below), after a fix-round finding (final review: I3) caught it sending a
 * scalar it had no business touching.
 *
 * `payload.markup` is `quotationFromAllocation.ts`'s own header explaining why it is ALWAYS `0`
 * on creation: the line `sell_unit`s already carry the margin, so a header markup on top would
 * charge it twice. That reasoning has nothing to do with re-pricing an EXISTING draft, whose
 * markup is now a user-editable per-quotation rate (task 1, `TotalsPanel`'s markup field) — a
 * purchaser sets 5% on `/quote`, changes an allocation on `/compare`, and this PUT used to send
 * `markup: 0` right over it with no warning, because that is what a brand-new payload always
 * carries. `markup` is a RATE the re-price caller has no opinion about, so it is omitted from the
 * PUT entirely rather than echoed from a payload that was never priced with it in mind. Creation
 * (the `callPost` branch below) is unaffected: it sends the whole payload including `markup: 0`
 * exactly as before, because there the value is correct BY CONSTRUCTION.
 */
async function repriceQuotation(existing: string, enquiryId: string, payload: CreateQuotationPayload): Promise<string> {
  rememberEnquiry(existing, enquiryId)
  await putQuotation(existing, {
      items: payload.items,
      net_total: payload.net_total,
      total: payload.total,
      grand_total: payload.grand_total,
      supplier_quote_comparison: payload.supplier_quote_comparison,
      // The PO side of the header moves with the lines (`completeQuotationPayload`).
      ...(payload.total_po_cost !== undefined ? { total_po_cost: payload.total_po_cost } : {}),
      ...(payload.po_currency !== undefined ? { po_currency: payload.po_currency, po_exchange_rate: payload.po_exchange_rate ?? 1 } : {}),
      ...(payload.line_markup !== undefined ? { line_markup: payload.line_markup } : {}),
  })
  return existing
}

interface CreateQuotationVars {
  enquiryId: string
  payload: CreateQuotationPayload
  existing?: string | null
  /** The enquiry's customer reference — quoted back on the document (`completeQuotationPayload`). */
  customerReferenceNo?: string | null
  /** The enquiry's company — mandatory on the quotation since 2026-09-17. */
  company?: string | null
  /** The enquiry's type — mandatory on the quotation since 2026-09-17 (pm). */
  enquiryType?: string | null
  /**
   * The enquiry's status as the caller last read it. When given, the enquiry is moved to
   * `Comparison Done` after the write — the same stopgap the comparison save applies, and the
   * ONLY thing that moves a contract-priced provision enquiry off `Open`: it never had an RFQ,
   * so without this its Requirements card kept opening the RFQ builder while the quotation sat
   * on `/quote/:id`. Omit it and the status is left alone. See `advanceStatus.ts`.
   */
  currentStatus?: EnquiryStatus
}

async function createOrRepriceQuotation({ enquiryId, payload: raw, existing, currentStatus, customerReferenceNo, company, enquiryType }: CreateQuotationVars): Promise<string> {
  // Every creator's body, completed the same way — see `lib/quotationPayload.ts`.
  const payload = completeQuotationPayload(raw, { baseCurrency: getBaseCurrency(), customerReferenceNo, company, enquiryType })
  /*
    Pre-flight for the doctype's mandatory header (2026-09-17). Checked here, before the POST,
    so what the purchaser reads is which enquiry fields to fill rather than the server's 417.
  */
  if (!existing) {
    const missing = missingQuotationMandatory(payload)
    if (missing.length > 0) {
      throw new Error(`The quotation cannot be created yet — the server requires ${describeMandatory(missing)}. Set ${missing.length === 1 ? 'it' : 'them'} on the enquiry header (RFQ builder) and build again.`)
    }
  }
  /*
    RE-PRICE an existing draft rather than refusing.

    The old flow could only warn — `build_quotation` never rebuilt an existing document from a
    new allocation, so changing the split left the quotation showing the old one and the screen
    offered a dead end. We build the lines ourselves now, and `update_quotation` (PUT) replaces
    `items` WHOLESALE — verified live on QTN-2026-00004, qty 4 -> 2, lines replaced and the
    server recomputed the total from them.

    Only a DRAFT. A quotation already sent to a customer must not be silently rewritten under
    them; that is a revision, and it has its own flow (`log_revision_request`/`apply_revisions`).
    The caller decides, and passes no `existing` when it must not be touched.
  */
  let name: string
  if (existing) {
    name = await repriceQuotation(existing, enquiryId, payload)
  } else {
    const w = await callPost<{ name?: string }>('quotation', 'create_quotation', { data: payload })
    name = str(w?.name)
    if (name === '') {
      // A created document we cannot name is one nobody can open again. Better to fail loudly
      // than to navigate to a quote screen with nothing to load.
      throw new Error('quotation.create_quotation returned no quotation name')
    }
  }
  // After the quotation is safely written — a failed status write is swallowed inside, so it
  // can never report a saved quotation as unsaved.
  if (currentStatus) await advanceToComparisonDone(enquiryId, currentStatus)
  return name
}

export function useCreateQuotation(options?: {
  onSuccess?: (name: string) => void
  onError?: (error: BosunApiError) => void
}) {
  const queryClient = useQueryClient()
  return useMutation<string, BosunApiError, CreateQuotationVars>({
    mutationFn: createOrRepriceQuotation,
    onSuccess: (name, { enquiryId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.existingQuotationForEnquiry(enquiryId) })
      /*
        ---- The Requirements board, too ----

        A quotation changes more than the quote screen: the enquiry has moved on, and the board is
        where a purchaser sees that. Without these it went on showing the old status and the old
        reply counts until something else happened to refetch — reported for contract-priced
        quotations, which reach the board by no other route at all (no RFQ, no supplier quotation,
        so none of `useCreateSupplierQuotation`'s invalidations ever fire for them).

        The same three `useCreateSupplierQuotation` invalidates, and for the same reason.
      */
      void queryClient.invalidateQueries({ queryKey: ['enquiries'] })
      void queryClient.invalidateQueries({ queryKey: ['board-replies'] })
      void queryClient.invalidateQueries({ queryKey: qk.enquiry(enquiryId) })
      options?.onSuccess?.(name)
    },
    onError: (error) => options?.onError?.(error),
  })
}
